import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "./AuthContext";
import { CollectionRecord, rowToStoredCollection, StoredCollection } from "../types/collection.model";

interface CollectionContextType {
  collections: StoredCollection[];
  addCollection: (name: string) => Promise<StoredCollection>;
  renameCollection: (id: string, name: string) => Promise<void>;
  deleteCollection: (id: string) => Promise<void>;
}

const CollectionContext = createContext<CollectionContextType | undefined>(undefined);

export const CollectionProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { session } = useAuth();
  const [collections, setCollections] = useState<StoredCollection[]>([]);

  useEffect(() => {
    if (!session) return;

    supabase.from('collections').select('*').then(({ data, error }) => {
      if (error || !data) return;
      setCollections((data as CollectionRecord[]).map(rowToStoredCollection));
    });
  }, [session]);

  const addCollection = async (name: string): Promise<StoredCollection> => {
    if (!session) throw new Error("Not signed in.");

    const { data, error } = await supabase
      .from('collections')
      .insert({ user_id: session.user.id, name })
      .select()
      .single();

    if (error || !data) throw new Error(error?.message ?? "Failed to create collection.");

    const storedCollection = rowToStoredCollection(data as CollectionRecord);
    setCollections(prev => [...prev, storedCollection]);
    return storedCollection;
  };

  const renameCollection = async (id: string, name: string): Promise<void> => {
    const { error } = await supabase.from('collections').update({ name }).eq('id', id);

    if (error) throw new Error(error.message);

    setCollections(prev => prev.map(c => c.id === id ? { ...c, name } : c));
  };

  const deleteCollection = async (id: string): Promise<void> => {
    const { error } = await supabase.from('collections').delete().eq('id', id);

    if (error) throw new Error(error.message);

    setCollections(prev => prev.filter(c => c.id !== id));
  };

  return (
    <CollectionContext.Provider value={{ collections, addCollection, renameCollection, deleteCollection }}>
      {children}
    </CollectionContext.Provider>
  );
};

export const useCollections = () => {
  const context = useContext(CollectionContext);

  if (context === undefined) {
    throw new Error('useCollections must be used within a CollectionProvider');
  }

  return context;
};
